import * as amplitude from "@amplitude/analytics-browser";
import { AnalyticsEventMap, AnalyticsEventName } from "../types/events";

let initPromise: Promise<void> | null = null;

export const initAmplitude = (): Promise<void> => {
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve) => {
    const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

    if (!apiKey) {
      console.warn(
        "[Amplitude] REACT_APP_AMPLITUDE_API_KEY is not configured. Analytics tracking is disabled.",
      );
      resolve();
      return;
    }

    amplitude.init(apiKey, {
      defaultTracking: {
        sessions: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
      },
    });

    resolve();
  });

  return initPromise;
};

export const trackEvent = <T extends AnalyticsEventName>(
  event: T,
  ...args: AnalyticsEventMap[T] extends Record<string, never>
    ? []
    : [properties: AnalyticsEventMap[T]]
): void => {
  const properties = args[0] ?? {};

  amplitude.track(event, properties);
};

export const identifyUserInAmplitude = async (
  userId: string,
  email?: string,
): Promise<void> => {
  if (initPromise) await initPromise;

  amplitude.setUserId(userId);

  if (!email) return;

  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest(
    "SHA-256",
    encoder.encode(email),
  );

  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  const identifyEvent = new amplitude.Identify().set("email_hash", hashHex);
  amplitude.identify(identifyEvent);
};
